import { renderPlan, type OfflineContextFactory, type RenderAudio } from '../audio/render';
import { sessionTiming } from '../av/sessionController';
import type { AvSession, Project } from '../domain/schemas';
import { sessionAudioPlan } from '../video/sessionAudio';

/**
 * Measured audio features for visualizers (Phase 4C) — real analysis of rendered audio,
 * computed once, offline and deterministically:
 *
 *  1. the session's music is rendered linearly (position 0 → session length, loop off)
 *     through `renderPlan` — the same instrument graph as playback and export, with
 *     seeded noise, so identical inputs give bit-identical samples;
 *  2. every 1/60 s: a Hann-windowed 1024-point FFT (32 log-spaced bands, 30 Hz–12 kHz, in
 *     dB normalised to the track's maximum over 60 dB), RMS and peak, low/mid/high;
 *  3. waveform samples at 4 kHz; prefix sums of every series so any smoothing window costs
 *     O(1) at any timestamp.
 *
 * Features are indexed by *session position* — the coordinate the clock folds loops into
 * — so looped playback, seeking and export ranges read the same data. Nothing here runs
 * per displayed frame; evaluation is in `features.ts`.
 */

export const ANALYSIS_VERSION = 1;
export const ANALYSIS_RATE = 24000;
export const FFT_SIZE = 1024;
/** 400 samples at 24 kHz = 1/60 s. */
export const HOP = 400;
export const HOP_SEC = HOP / ANALYSIS_RATE;
export const BAND_COUNT = 32;
export const BAND_MIN_HZ = 30;
export const BAND_MAX_HZ = 12000;
export const WAVE_RATE = 4000;
/** Longest analysed session (the video export limit). */
export const MAX_ANALYSIS_SECONDS = 300;
const DB_RANGE = 60;

/** Series indices: bands 0–31, then derived scalars. */
export const SERIES = { full: BAND_COUNT, low: BAND_COUNT + 1, mid: BAND_COUNT + 2, high: BAND_COUNT + 3, rms: BAND_COUNT + 4, peak: BAND_COUNT + 5 } as const;
export const SERIES_COUNT = BAND_COUNT + 6;
export const LOW_MAX_HZ = 250;
export const MID_MAX_HZ = 2000;

export interface AudioFeatures {
  version: number;
  durationSec: number;
  hopSec: number;
  frames: number;
  /** One series per band and scalar (0–1), `frames` values each. */
  series: Float32Array[];
  /** Prefix sums: prefix[s][k] = sum of series[s][0..k-1]. */
  prefix: Float64Array[];
  /** Mono waveform at WAVE_RATE, normalised to the track's peak (±32767). */
  waveform: Int16Array;
  waveRate: number;
  /** Band centre frequencies (Hz). */
  bandHz: Float32Array;
  /** Wall-clock cost of rendering and analysing (diagnostics). */
  renderMs: number;
  analyzeMs: number;
}

// ------------------------------------------------------------------ FFT

/** In-place iterative radix-2 FFT (re/im of length n, a power of two). */
export function fft(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j]!, re[i]!];
      [im[i], im[j]] = [im[j]!, im[i]!];
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const a = i + k;
        const b = a + len / 2;
        const tr = re[b]! * cr - im[b]! * ci;
        const ti = re[b]! * ci + im[b]! * cr;
        re[b] = re[a]! - tr;
        im[b] = im[a]! - ti;
        re[a] = re[a]! + tr;
        im[a] = im[a]! + ti;
        const ncr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = ncr;
      }
    }
  }
}

/** Band edges (Hz) and the FFT bins each band sums (at least one bin per band). */
export function bandLayout(rate = ANALYSIS_RATE, size = FFT_SIZE) {
  const edges: number[] = [];
  for (let b = 0; b <= BAND_COUNT; b++) edges.push(BAND_MIN_HZ * (BAND_MAX_HZ / BAND_MIN_HZ) ** (b / BAND_COUNT));
  const binHz = rate / size;
  const bins: [number, number][] = [];
  const centers = new Float32Array(BAND_COUNT);
  for (let b = 0; b < BAND_COUNT; b++) {
    let lo = Math.max(1, Math.round(edges[b]! / binHz));
    let hi = Math.max(lo, Math.round(edges[b + 1]! / binHz) - 1);
    hi = Math.min(hi, size / 2 - 1);
    lo = Math.min(lo, hi);
    bins.push([lo, hi]);
    centers[b] = Math.sqrt(edges[b]! * edges[b + 1]!);
  }
  return { edges, bins, centers };
}

const yieldNow = () => new Promise<void>((r) => setTimeout(r, 0));

/**
 * Analyses mono samples (pure and deterministic). `onProgress` and `signal` allow chunked,
 * cancellable processing; the result does not depend on them.
 */
export async function analyzeSamples(mono: Float32Array, rate = ANALYSIS_RATE, options: { signal?: AbortSignal; onProgress?: (f: number) => void } = {}): Promise<AudioFeatures> {
  const t0 = performance.now();
  if (rate !== ANALYSIS_RATE) throw new Error(`Analysis expects ${ANALYSIS_RATE} Hz samples.`);
  const frames = Math.max(1, Math.ceil(mono.length / HOP));
  const series = Array.from({ length: SERIES_COUNT }, () => new Float32Array(frames));
  const { bins, centers } = bandLayout(rate, FFT_SIZE);
  const window = new Float64Array(FFT_SIZE);
  for (let i = 0; i < FFT_SIZE; i++) window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (FFT_SIZE - 1));
  const re = new Float64Array(FFT_SIZE);
  const im = new Float64Array(FFT_SIZE);
  const bandDb = new Float32Array(frames * BAND_COUNT);
  const rms = new Float32Array(frames);
  const peak = new Float32Array(frames);
  let maxDb = -Infinity;
  for (let k = 0; k < frames; k++) {
    const start = k * HOP - FFT_SIZE / 2;
    let sq = 0;
    let pk = 0;
    for (let i = 0; i < FFT_SIZE; i++) {
      const j = start + i;
      const v = j >= 0 && j < mono.length ? mono[j]! : 0;
      sq += v * v;
      const a = Math.abs(v);
      if (a > pk) pk = a;
      re[i] = v * window[i]!;
      im[i] = 0;
    }
    rms[k] = Math.sqrt(sq / FFT_SIZE);
    peak[k] = pk;
    fft(re, im);
    for (let b = 0; b < BAND_COUNT; b++) {
      const [lo, hi] = bins[b]!;
      let power = 0;
      for (let i = lo; i <= hi; i++) power += re[i]! * re[i]! + im[i]! * im[i]!;
      const db = 10 * Math.log10(power / (hi - lo + 1) + 1e-12);
      bandDb[k * BAND_COUNT + b] = db;
      if (db > maxDb) maxDb = db;
    }
    if (k % 900 === 899) {
      options.signal?.throwIfAborted();
      options.onProgress?.(k / frames);
      await yieldNow();
    }
  }
  // Normalisation: bands relative to the track's loudest band over 60 dB; RMS and peak to their maxima.
  const floor = maxDb - DB_RANGE;
  let maxRms = 0;
  let maxPeak = 0;
  for (let k = 0; k < frames; k++) {
    maxRms = Math.max(maxRms, rms[k]!);
    maxPeak = Math.max(maxPeak, peak[k]!);
  }
  const lowBands: number[] = [];
  const midBands: number[] = [];
  const highBands: number[] = [];
  for (let b = 0; b < BAND_COUNT; b++) (centers[b]! < LOW_MAX_HZ ? lowBands : centers[b]! < MID_MAX_HZ ? midBands : highBands).push(b);
  const mean = (k: number, list: number[]) => list.reduce((s, b) => s + series[b]![k]!, 0) / Math.max(1, list.length);
  for (let k = 0; k < frames; k++) {
    for (let b = 0; b < BAND_COUNT; b++) {
      const v = Number.isFinite(floor) ? (bandDb[k * BAND_COUNT + b]! - floor) / DB_RANGE : 0;
      series[b]![k] = Math.min(1, Math.max(0, v));
    }
    series[SERIES.low]![k] = mean(k, lowBands);
    series[SERIES.mid]![k] = mean(k, midBands);
    series[SERIES.high]![k] = mean(k, highBands);
    series[SERIES.rms]![k] = maxRms > 0 ? rms[k]! / maxRms : 0;
    series[SERIES.peak]![k] = maxPeak > 0 ? peak[k]! / maxPeak : 0;
    series[SERIES.full]![k] = series[SERIES.rms]![k]!;
  }
  const prefix = series.map((s) => {
    const p = new Float64Array(s.length + 1);
    for (let i = 0; i < s.length; i++) p[i + 1] = p[i]! + s[i]!;
    return p;
  });
  // Waveform: block averages (rate / WAVE_RATE samples), normalised to the peak.
  const factor = rate / WAVE_RATE;
  const waveLen = Math.ceil(mono.length / factor);
  const waveform = new Int16Array(waveLen);
  const scale = maxPeak > 0 ? 32767 / maxPeak : 0;
  for (let i = 0; i < waveLen; i++) {
    let s = 0;
    let n = 0;
    for (let j = i * factor; j < Math.min(mono.length, (i + 1) * factor); j++, n++) s += mono[j]!;
    waveform[i] = Math.max(-32767, Math.min(32767, Math.round((n ? s / n : 0) * scale)));
  }
  // An abort during the last (partial) chunk is honoured too: a cancelled analysis never resolves.
  options.signal?.throwIfAborted();
  options.onProgress?.(1);
  return { version: ANALYSIS_VERSION, durationSec: mono.length / rate, hopSec: HOP_SEC, frames, series, prefix, waveform, waveRate: WAVE_RATE, bandHz: centers, renderMs: 0, analyzeMs: Math.round(performance.now() - t0) };
}

export class AnalysisUnavailable extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AnalysisUnavailable';
  }
}

/** Length analysed for a session (its full length), or an error when it cannot be analysed. */
export function analysisSeconds(project: Project, session: AvSession): number {
  const seconds = sessionTiming(project, session).durationSec;
  if (!(seconds > 0)) throw new AnalysisUnavailable('The session has no length to analyse.');
  if (seconds > MAX_ANALYSIS_SECONDS) throw new AnalysisUnavailable(`Audio analysis is limited to ${MAX_ANALYSIS_SECONDS / 60} minutes; this session is ${Math.round(seconds)} s.`);
  return seconds;
}

/**
 * Renders the session's music linearly (position 0 → session length, loop off) and
 * analyses it. Deterministic for identical project music, session music source and length.
 */
export async function analyzeSession(
  project: Project,
  session: AvSession,
  options: { audio?: RenderAudio; createContext?: OfflineContextFactory; signal?: AbortSignal; onProgress?: (f: number) => void } = {},
): Promise<AudioFeatures> {
  const seconds = analysisSeconds(project, session);
  const linear: AvSession = { ...session, loop: { ...session.loop, enabled: false }, endBehavior: 'stop' };
  const plan = sessionAudioPlan(project, linear, 0, seconds);
  const t0 = performance.now();
  const rendered = await renderPlan(project, plan, plan.wanted, Math.ceil(seconds * ANALYSIS_RATE), options.createContext, ANALYSIS_RATE, {
    audio: options.audio,
    signal: options.signal,
    onProgress: (f) => options.onProgress?.(f * 0.7),
  });
  const renderMs = Math.round(performance.now() - t0);
  options.signal?.throwIfAborted();
  const b = rendered.buffer;
  const mono = new Float32Array(b.length);
  for (let c = 0; c < b.numberOfChannels; c++) {
    const ch = b.getChannelData(c);
    for (let i = 0; i < mono.length; i++) mono[i]! += ch[i]! / b.numberOfChannels;
  }
  const features = await analyzeSamples(mono, ANALYSIS_RATE, { signal: options.signal, onProgress: (f) => options.onProgress?.(0.7 + 0.3 * f) });
  return { ...features, renderMs };
}
